import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { toast } from 'sonner';

type CapLead = {
  id: string; company_name: string; contact_name: string | null; email: string;
  phone: string | null; cnpj: string | null; lines_estimate: number | null;
  monthly_spend_estimate: string | null; account_file_name: string | null;
  converted_lead_id: string | null; created_at: string;
};

export default function Captura() {
  const [leads, setLeads] = useState<CapLead[]>([]);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const d = await api.get<{ leads: CapLead[] }>('/landing/leads');
    setLeads(d.leads);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function onConvert(id: string) {
    setBusy(true);
    try {
      const r = await api.post<{ leadId: string; already?: boolean }>(`/landing/leads/${id}/convert`);
      toast.success(r.already ? 'Já estava convertido.' : 'Convertido em lead no funil.');
      await load();
      navigate(`/painel/leads/${r.leadId}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Falha ao converter');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Captura</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Leads da landing ({leads.length})</h1>
      </header>
      {leads.length === 0 ? (
        <p className="mt-4 rounded-lg border border-[var(--border)] bg-white p-6 text-sm text-[var(--text-dim)]">
          Nenhuma captura ainda. Todo envio do form da landing aparece aqui, com a conta anexada (quando o lead mandar), pronta para converter no funil.
        </p>
      ) : (
        <div className="mt-4 rounded-lg border border-[var(--border)] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left text-xs text-[var(--text-dim)] uppercase tracking-wider">
                <th className="px-4 py-3 font-medium">Empresa</th>
                <th className="px-4 py-3 font-medium">Contato</th>
                <th className="px-4 py-3 text-right font-medium">Linhas/Gasto</th>
                <th className="px-4 py-3 font-medium">Conta</th>
                <th className="px-4 py-3 text-right font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-4 py-3">
                    <span className="block font-medium">{l.company_name}</span>
                    <span className="text-xs text-[var(--text-dim)]">{new Date(l.created_at).toLocaleString('pt-BR')}</span>
                  </td>
                  <td className="px-4 py-3 text-[var(--text-dim)]">
                    <span className="block">{l.email}</span>
                    {l.phone && <span className="text-xs">{l.phone}</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-[var(--text-dim)]">
                    {l.lines_estimate ? `${l.lines_estimate} linhas` : ''}
                    {l.monthly_spend_estimate ? ` · ${Number(l.monthly_spend_estimate).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : ''}
                  </td>
                  <td className="px-4 py-3 text-xs text-[var(--text-dim)]">{l.account_file_name ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {l.converted_lead_id ? (
                      <button onClick={() => navigate(`/painel/leads/${l.converted_lead_id}`)} className="h-8 rounded-md border border-[var(--border-strong)] px-3 text-xs hover:bg-[var(--bg-subtle)]">Ver lead</button>
                    ) : (
                      <button onClick={() => onConvert(l.id)} disabled={busy} className="h-8 rounded-md bg-[var(--accent)] px-3 text-xs font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">Converter</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
